import {
  DataRoomFolder,
  useAllDataRoomFoldersQuery,
} from "../graphql/generated";
import { FolderIcon } from "@heroicons/react/20/solid";
import {
  Combobox,
  ComboboxInput,
  Popover,
  Transition,
} from "@headlessui/react";
import { useRef, useState } from "react";
import useGqlClient from "../hooks/useGqlClient";
import Loading from "./Loading";
import { classNames } from "../utils/cn";
import { useParams } from "react-router-dom";
import {
  buildFolderHierarchy,
  FolderWrapper,
} from "../utils/buildFolderHierarchy";
import { getDataRoomIndex } from "../utils/getDataRoomIndex";
import { ChevronDownIcon } from "lucide-react";
import clsx from "clsx";
import { truncateText } from "../utils/truncateText";

export function AddFolderPill(props: {
  onFolderSelected: (folder: DataRoomFolder) => void;
  selectedFolder?: Pick<DataRoomFolder, "id" | "name"> | null;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Popover className="relative">
      <Popover.Button
        ref={buttonRef}
        className="relative cursor-pointer hover:border-gray-400 bg-gray-100 hover:shadow-sm flex items-center rounded-md w-full border px-2 py-1"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <FolderIcon
              className={clsx(
                "h-5 w-5 flex-shrink-0",
                props.selectedFolder ? "text-blue-700/70" : "text-gray-600/80",
              )}
            />
            <p className="text-xs select-none font-semibold text-gray-600 ml-1.5 truncate">
              {props.selectedFolder
                ? props.selectedFolder.name === ""
                  ? "Home"
                  : props.selectedFolder.name
                : "Select folder"}
            </p>
          </div>

          <ChevronDownIcon className="h-5 w-5 text-gray-500/80 justify-end ml-2" />
        </div>
        <Transition
          enter="ease-in-out duration-50"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <Popover.Panel className="z-20 absolute mt-5 min-w-[500px] -translate-x-1/3 transform px-4">
            <PopOut
              onFolderSelected={(folder) => {
                props.onFolderSelected(folder);
                // buttonRef.current?.click();
              }}
            />
          </Popover.Panel>
        </Transition>
      </Popover.Button>
    </Popover>
  );
}

const PopOut = ({
  onFolderSelected,
}: {
  onFolderSelected: (folder: DataRoomFolder) => void;
}) => {
  const [query, setQuery] = useState("");

  return (
    <div className="border rounded-md ml-10">
      <Combobox
        onChange={(f: DataRoomFolder) => {
          onFolderSelected(f);
        }}
      >
        <div className="">
          <div className="relative w-full cursor-default overflow-hidden rounded-t-md bg-white text-left border-b border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <ComboboxInput
              placeholder="Search folders..."
              autoFocus
              className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </div>

          <div className="bg-white rounded-b-md">
            <SearchResults searchTerm={query} />
          </div>
        </div>
      </Combobox>
    </div>
  );
};

interface SearchResultsProps {
  searchTerm: string;
}

function SearchResults(props: SearchResultsProps) {
  const client = useGqlClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const dataRoomAllFoldersQuery = useAllDataRoomFoldersQuery(client, {
    dealId: activeDealId ?? "",
  });

  if (dataRoomAllFoldersQuery.isError) {
    return (
      <div className=" bg-white flex justify-center h-10 items-center">
        <p className="text-gray-500 text-xs">Something went wrong</p>
      </div>
    );
  }

  if (dataRoomAllFoldersQuery.isLoading || !dataRoomAllFoldersQuery.data) {
    return (
      <div className="flex bg-white justify-center h-10 items-center">
        <Loading />
      </div>
    );
  }

  const folders = buildFolderHierarchy(
    dataRoomAllFoldersQuery.data.deal.dataRoom.allFolders,
    dataRoomAllFoldersQuery.data.deal.indexing,
  );

  return (
    <>
      <Combobox.Options
        static
        className="max-h-80 scroll-py-2 divide-y  divide-gray-100 overflow-y-auto"
      >
        <li className="list-none">
          <ul className="text-sm text-gray-700">
            {folders
              // .filter((f) =>
              //   f.folder.name
              //     .toLowerCase()
              //     .includes(props.searchTerm.toLowerCase()),
              // )
              .map((folder) => {
                return (
                  <FolderContent
                    folder={folder}
                    searchTerm={props.searchTerm}
                    indexingEnabled={dataRoomAllFoldersQuery.data.deal.indexing}
                    key={folder.folder.id}
                    depth={0}
                  />
                );
              })}
          </ul>
        </li>
      </Combobox.Options>
    </>
  );
}

function hasChild(folder: FolderWrapper, searchTerm: string) {
  if (folder.folder.name.toLowerCase().includes(searchTerm.toLowerCase())) {
    return true;
  }

  for (const child of folder.folders) {
    if (hasChild(child, searchTerm)) {
      return true;
    }
  }

  return false;
}

function FolderContent(props: {
  folder: FolderWrapper;
  searchTerm: string;
  depth: number;
  indexingEnabled: boolean;
}) {
  const folder = props.folder;

  if (props.searchTerm.length > 2) {
    if (!hasChild(folder, props.searchTerm)) {
      return null;
    }
  }

  return (
    <div>
      <Combobox.Option
        key={folder.folder.id}
        value={folder.folder}
        className={({ active }) =>
          classNames(
            "cursor-pointer select-none  px-3 py-2",
            active ? "bg-gray-200/70" : "",
          )
        }
        style={{ paddingLeft: `${props.depth * 0.5}rem` }}
      >
        {({ active }) => (
          <div className="">
            <div className="flex items-center gap-x-1">
              {props.indexingEnabled && folder.folder.index ? (
                <p className="text-gray-500 text-xs">
                  {getDataRoomIndex(
                    folder.folder.index?.sortIndex,
                    folder.folder.parentFolders as DataRoomFolder[],
                    "folder",
                  )}
                </p>
              ) : null}
              <FolderIcon className="ml-1 h-5 w-5 text-blue-700/70 flex-shrink-0" />
              <p
                className="font-bold leading-tight mt-0.5 text-sm text-left"
                title={folder.folder.name}
              >
                {folder.folder.name === ""
                  ? "Home"
                  : truncateText(folder.folder.name, 40)}
              </p>
            </div>
          </div>
        )}
      </Combobox.Option>
      <>
        {folder.folders.map((child) => {
          return (
            <FolderContent
              folder={child}
              searchTerm={props.searchTerm}
              key={child.folder.id}
              depth={props.depth + 1}
              indexingEnabled={props.indexingEnabled}
            />
          );
        })}
      </>
    </div>
  );
}
